import { Wifi, WifiOff } from 'lucide-react';
import { useSocketStatus } from '../../hooks/useSocketStatus';
import { useOnline } from '../../hooks/useOnline';

/**
 * Tiny status pill that lives next to the user pill in the TopBar.
 * Two signals feed it: the Socket.IO connection (real-time event
 * broadcast) and the browser's online flag. Green = both good, amber
 * = browser online but socket reconnecting, red = browser offline or
 * socket gave up.
 *
 * The PWA service worker queues KitIssued / KitReturned POSTs while
 * red, so a red badge isn't catastrophic — the offline banner below
 * the TopBar explains what's happening.
 */
export function LiveStatusBadge() {
  const online = useOnline();
  const sock = useSocketStatus();

  const state: 'live' | 'reconnecting' | 'offline' = !online
    ? 'offline'
    : sock === 'connected'
      ? 'live'
      : sock === 'reconnecting'
        ? 'reconnecting'
        : 'offline';

  const tone =
    state === 'live'
      ? 'bg-success-soft/15 text-success-soft-fg ring-success-soft/30'
      : state === 'reconnecting'
        ? 'bg-warning-soft/15 text-warning-soft-fg ring-warning-soft/30'
        : 'bg-danger-soft/15 text-danger-soft-fg ring-danger-soft/30';
  const label =
    state === 'live'
      ? 'Live'
      : state === 'reconnecting'
        ? 'Reconnecting'
        : 'Offline';
  const dotTone =
    state === 'live'
      ? 'bg-success'
      : state === 'reconnecting'
        ? 'bg-warning'
        : 'bg-danger';
  const pingTone = state === 'live' ? 'bg-success' : '';
  const title =
    state === 'live'
      ? 'Real-time sync is connected. Events broadcast across all sessions.'
      : state === 'reconnecting'
        ? 'Re-establishing the live connection. Reads still work; signed events queue locally.'
        : 'No live connection. Signed events will queue and replay automatically when the network returns.';

  return (
    <span
      role="status"
      title={title}
      aria-label={title}
      className={`inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-[11px] font-semibold uppercase tracking-wider ring-1 ${tone}`}
    >
      <span className="relative inline-flex h-2 w-2 items-center justify-center">
        {state === 'live' ? (
          <span
            aria-hidden
            className={`absolute inset-0 animate-ping rounded-full opacity-70 ${pingTone}`}
          />
        ) : null}
        <span className={`relative h-2 w-2 rounded-full ${dotTone}`} />
      </span>
      <span className="hidden sm:inline">{label}</span>
      {state === 'offline' ? (
        <WifiOff className="h-3 w-3 sm:hidden" aria-hidden />
      ) : (
        <Wifi className="h-3 w-3 sm:hidden" aria-hidden />
      )}
    </span>
  );
}
